import {
  CitationViewItem,
  mergeCitationItems,
  normalizeNewCitationPacket,
  formatDocLabel,
  formatTabularLabel,
} from "./citations";

export type MessagePart =
  | { type: "text"; content: string }
  | { type: "citation"; content: unknown }
  | {
      type: "plots_data";
      content?:
        | number
        | { index?: number; indices?: number[]; count?: number }
        | null;
    };

export type InlineBadge = {
  n: number;
  project_id?: string | null;
  file_id?: string;
  original_file?: string;
  page_number?: number;
  sheet_name?: string;
  start_row?: number;
  hoverText: string;
  kind?: "document" | "tabular" | "web";
  url?: string;
  title?: string;
};

export function buildMessageFromParts(
  parts: MessagePart[],
  projectId?: string | number | null
): { text: string; badges: InlineBadge[]; merged: CitationViewItem[] } {
  let text = "";
  const badges: InlineBadge[] = [];
  let merged: CitationViewItem[] = [];
  let n = 1;

  let imgCursor = 0;

  const toExplicitIndices = (
    payload: MessagePart["content"]
  ): number[] | null => {
    if (typeof payload === "number") return [payload];
    if (payload && typeof payload === "object") {
      const p = payload as { index?: unknown; indices?: unknown };
      if (typeof p.index === "number") return [p.index];
      if (Array.isArray(p.indices)) {
        return p.indices.filter((v) => Number.isFinite(v)) as number[];
      }
    }
    return null;
  };

  const toCount = (payload: MessagePart["content"]): number | null => {
    if (payload && typeof payload === "object") {
      const p = payload as { count?: unknown };
      if (typeof p.count === "number") {
        return Math.max(0, Math.floor(p.count));
      }
    }
    return null;
  };

  for (const part of parts || []) {
    if (part.type === "text") {
      text += part.content || "";
      continue;
    }

    if (part.type === "citation") {
      const norm = normalizeNewCitationPacket(part.content as any);
      merged = mergeCitationItems(merged, norm);

      const lines: string[] = [];
      for (const it of norm) {
        if (it.kind === "web") {
          const host = (() => {
            try {
              return new URL(it.url).hostname;
            } catch {
              return it.url;
            }
          })();
          const title = it.title || host;
          lines.push(`${title} — ${host}`);
        } else if (it.kind === "document") {
          lines.push(formatDocLabel(it));
        } else {
          lines.push(formatTabularLabel(it));
        }
      }
      const hoverText = lines.map((l) => `• ${l}`).join("\n");

      let primary: Partial<InlineBadge> = {};
      const first = norm[0];
      if (first) {
        if (first.kind === "document") {
          const pageAnchor =
            first.overall?.[0] ??
            first.page_ranges?.[0]?.[0] ??
            first.pages?.[0];
          primary = {
            kind: "document",
            file_id: first.file_id,
            original_file: first.file_name,
            page_number: pageAnchor ?? undefined,
          };
        } else if (first.kind === "tabular") {
          const [sheet, payload] =
            Object.entries(first.sheet_ranges || {})[0] || [];
          const rowAnchor = payload
            ? payload.overall?.[0] ?? payload.ranges?.[0]?.[0]
            : undefined;
          primary = {
            kind: "tabular",
            file_id: first.file_id,
            original_file: first.file_name,
            sheet_name: sheet,
            start_row: rowAnchor ?? undefined,
          };
        } else {
          primary = { kind: "web", url: first.url, title: first.title };
        }
      }

      badges.push({
        n,
        project_id: projectId != null ? String(projectId) : null,
        hoverText,
        ...primary,
      });

      text += ` {[CIT:${n}]}`;
      n++;
      continue;
    }

    if (part.type === "plots_data") {
      const explicit = toExplicitIndices(part.content ?? null);
      if (explicit && explicit.length > 0) {
        // Emit tokens for the explicit indices in order
        for (const idx of explicit) {
          text += ` {[IMG:${idx}]}`;
          // Keep the cursor ahead of the largest seen index to avoid collisions
          if (idx >= imgCursor) imgCursor = idx + 1;
        }
      } else {
        // No explicit indices → rely on cursor
        const count = toCount(part.content);
        const emitCount = count ?? 1; // default to one image token
        for (let i = 0; i < emitCount; i++) {
          text += ` {[IMG:${imgCursor++}]}`;
        }
      }
      continue;
    }
  }

  return { text, badges, merged };
}
